import { BASE } from './api';
import { applyCssTheme, setupDomThemeToggle } from './theme';

// ─── API documentation view (DOM) ───────────────────────────────────────────
// Rendered into #api-view when the route is "/api". Static reference for the
// backend REST API. The endpoint list mirrors backend/src/server.ts.

// In production BASE is "" (same-origin); show the live origin in examples so
// the curl commands are copy-pasteable.
const HOST = BASE || window.location.origin;

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface Endpoint {
  method:   Method;
  path:     string;
  summary:  string;
  detail?:  string;
  request?: string;   // curl example
  status:   string;   // success status (+ notable errors)
  response: string;   // example response body ('' = empty)
}

interface Group {
  title:     string;
  endpoints: Endpoint[];
}

const GROUPS: Group[] = [
  {
    title: 'Pages',
    endpoints: [
      {
        method:  'GET',
        path:    '/api/pages',
        summary: 'List all pages, newest first. node_count is the number of nodes the page owns.',
        request: `curl ${HOST}/api/pages`,
        status:  '200 OK',
        response: `[
  {
    "id": "019f0463-8a77-746d-bc2d-5e5190ec7eb8",
    "title": "My First Map",
    "created_at": "2026-06-26T14:44:20.215Z",
    "node_count": 12
  }
]`,
      },
      {
        method:  'POST',
        path:    '/api/pages',
        summary: 'Create a page. The returned id is a UUID v7 (time-ordered).',
        detail:  'Body: { "title": string }. Returns 400 if title is missing or blank.',
        request: `curl -X POST ${HOST}/api/pages \\
  -H 'Content-Type: application/json' \\
  -d '{"title":"Roadmap"}'`,
        status:  '201 Created · 400 if title blank',
        response: `{
  "id": "019f0470-6784-7f72-834c-b4a72e33ebd2",
  "title": "Roadmap",
  "created_at": "2026-06-26T15:01:09.882Z",
  "node_count": 0
}`,
      },
      {
        method:  'PATCH',
        path:    '/api/pages/:id',
        summary: 'Rename a page.',
        detail:  'Body: { "title": string }. Returns 400 if title is blank.',
        request: `curl -X PATCH ${HOST}/api/pages/019f0470-6784-7f72-834c-b4a72e33ebd2 \\
  -H 'Content-Type: application/json' \\
  -d '{"title":"Q3 Roadmap"}'`,
        status:  '204 No Content · 400 if title blank',
        response: '',
      },
      {
        method:  'DELETE',
        path:    '/api/pages/:id',
        summary: 'Delete a page and all of its nodes (cascade).',
        request: `curl -X DELETE ${HOST}/api/pages/019f0470-6784-7f72-834c-b4a72e33ebd2`,
        status:  '204 No Content',
        response: '',
      },
    ],
  },
  {
    title: 'Nodes',
    endpoints: [
      {
        method:  'GET',
        path:    '/api/pages/:id/nodes',
        summary: 'List every node in a page.',
        detail:  'Nodes form a tree via parent_id (root nodes have parent_id: null), ordered by child_order. x / y are world coordinates (null until first positioned).',
        request: `curl ${HOST}/api/pages/019f0463-8a77-746d-bc2d-5e5190ec7eb8/nodes`,
        status:  '200 OK',
        response: `[
  {
    "id": "n_mqv231ck_8jannx",
    "label": "Backend API",
    "description": "Auth, DB, endpoints.",
    "status": "in-progress",
    "parent_id": null,
    "child_order": 0,
    "x": 640,
    "y": 360
  }
]`,
      },
      {
        method:  'POST',
        path:    '/api/pages/:id/nodes',
        summary: 'Create a node in a page.',
        detail:  'Body: { "label": string, "parent_id": string | null }. Use parent_id: null for a root node, or a node id to attach it as a child. New nodes start with description "" and status "todo".',
        request: `curl -X POST ${HOST}/api/pages/019f0463-8a77-746d-bc2d-5e5190ec7eb8/nodes \\
  -H 'Content-Type: application/json' \\
  -d '{"label":"Task A","parent_id":null}'`,
        status:  '201 Created · 400 if label blank',
        response: `{
  "id": "n_mqv231ck_8jannx",
  "label": "Task A",
  "description": "",
  "status": "todo",
  "parent_id": null,
  "child_order": 0,
  "x": null,
  "y": null
}`,
      },
      {
        method:  'PATCH',
        path:    '/api/nodes/:id',
        summary: 'Update a node. Send any subset of the fields.',
        detail:  'Body: { x?, y?, status?, label?, description? }. x and y must be sent together (a position update). status is one of todo | in-progress | done | blocked.',
        request: `curl -X PATCH ${HOST}/api/nodes/n_mqv231ck_8jannx \\
  -H 'Content-Type: application/json' \\
  -d '{"status":"done","description":"Shipped 🎉"}'`,
        status:  '204 No Content',
        response: '',
      },
      {
        method:  'POST',
        path:    '/api/nodes/positions',
        summary: 'Bulk-update node positions in one request (used after a drag or auto-layout).',
        detail:  'Body: an array of { id, x, y }. Returns 400 if the body is not an array.',
        request: `curl -X POST ${HOST}/api/nodes/positions \\
  -H 'Content-Type: application/json' \\
  -d '[{"id":"n_mqv231ck_8jannx","x":640,"y":360}]'`,
        status:  '204 No Content · 400 if body is not an array',
        response: '',
      },
      {
        method:  'DELETE',
        path:    '/api/nodes/:id',
        summary: 'Delete a node and its entire subtree (cascade).',
        detail:  'Also deletes every event attached to the deleted nodes.',
        request: `curl -X DELETE ${HOST}/api/nodes/n_mqv231ck_8jannx`,
        status:  '204 No Content',
        response: '',
      },
    ],
  },
  {
    title: 'Events',
    endpoints: [
      {
        method:  'GET',
        path:    '/api/pages/:id/events',
        summary: 'Poll a page-wide event stream — every event across all of the page\'s nodes.',
        detail:  'Query: ?since=<cursor>. Omit since on the first call to seed the cursor (returns { "events": [], "cursor" }); pass the returned cursor on each subsequent call to receive only events created since, oldest first. Used for live polling. Pass an empty since (?since=) to get the full history — the cursor is "before everything".',
        request: `curl ${HOST}/api/pages/019f0463-8a77-746d-bc2d-5e5190ec7eb8/events?since=2026-06-30T15:01:09.882Z`,
        status:  '200 OK',
        response: `{
  "events": [
    {
      "id": "e_mqv24abc_x7k2p9",
      "node_id": "n_mqv231ck_8jannx",
      "status": "done",
      "note": "shipped",
      "author": "pierre",
      "label": null,
      "prev_label": null,
      "created_at": "2026-06-30T16:20:00.000Z"
    }
  ],
  "cursor": "2026-06-30T16:20:00.000Z"
}`,
      },
      {
        method:  'GET',
        path:    '/api/nodes/:id/events',
        summary: 'List a node\'s events, newest first.',
        detail:  'An event records an optional status change and/or a rename, plus extra information (note, author) that lives only on the event — never on the node. status is null for events that don\'t change status; label/prev_label are null unless the event renamed the node.',
        request: `curl ${HOST}/api/nodes/n_mqv231ck_8jannx/events`,
        status:  '200 OK',
        response: `[
  {
    "id": "e_mqv24abc_x7k2p9",
    "node_id": "n_mqv231ck_8jannx",
    "status": "blocked",
    "note": "waiting on vendor",
    "author": "pierre",
    "label": null,
    "prev_label": null,
    "created_at": "2026-06-30T15:01:09.882Z"
  }
]`,
      },
      {
        method:  'POST',
        path:    '/api/nodes/:id/events',
        summary: 'Send an event about a node.',
        detail:  'Body: { status?, note?, author?, label? }. All fields optional. A non-empty status (todo | in-progress | done | blocked) is recorded on the event AND updates the node\'s current status. A non-empty label that differs from the current name renames the node — the event keeps the previous name in prev_label. Omit both for an info-only event. note and author are stored only on the event.',
        request: `curl -X POST ${HOST}/api/nodes/n_mqv231ck_8jannx/events \\
  -H 'Content-Type: application/json' \\
  -d '{"label":"Backend API v2","note":"renamed for clarity","author":"pierre"}'`,
        status:  '201 Created',
        response: `{
  "id": "e_mqv24abc_x7k2p9",
  "node_id": "n_mqv231ck_8jannx",
  "status": null,
  "note": "renamed for clarity",
  "author": "pierre",
  "label": "Backend API v2",
  "prev_label": "Backend API",
  "created_at": "2026-06-30T15:01:09.882Z"
}`,
      },
    ],
  },
];

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function block(label: string, body: string): string {
  if (!body) return '';
  return `
    <div class="api-block">
      <span class="api-block-label">${label}</span>
      <pre><code>${escapeHtml(body)}</code></pre>
    </div>`;
}

function endpointHtml(e: Endpoint): string {
  return `
    <article class="api-endpoint">
      <div class="api-sig">
        <span class="api-method api-method-${e.method.toLowerCase()}">${e.method}</span>
        <code class="api-path">${escapeHtml(e.path)}</code>
      </div>
      <p class="api-summary">${escapeHtml(e.summary)}</p>
      ${e.detail ? `<p class="api-detail">${escapeHtml(e.detail)}</p>` : ''}
      <p class="api-status"><span class="api-block-label">Status</span> ${escapeHtml(e.status)}</p>
      ${block('Request', e.request ?? '')}
      ${block('Response', e.response)}
    </article>`;
}

export function renderApiDocs(): void {
  applyCssTheme();
  const root = document.getElementById('api-view') as HTMLElement;

  const groups = GROUPS.map(g => `
    <section class="api-group">
      <h2>${escapeHtml(g.title)}</h2>
      ${g.endpoints.map(endpointHtml).join('')}
    </section>`).join('');

  root.innerHTML = `
    <div class="api-shell">
      <header class="api-header">
        <h1><span class="app-logo" role="button" tabindex="0">Dictator</span> API</h1>
        <div class="api-header-actions">
          <button id="api-theme-toggle" class="theme-toggle-btn" type="button"></button>
          <button id="api-back-btn" type="button">← Pages</button>
        </div>
      </header>

      <section class="api-intro">
        <p>REST API backing the mind map. All responses are JSON; CORS is open
           (<code>Access-Control-Allow-Origin: *</code>) and <code>OPTIONS</code>
           preflight returns <code>204</code>.</p>
        <p><strong>Base URL</strong> <code>${escapeHtml(HOST)}</code></p>
        <p><strong>Data model.</strong> A <em>page</em> owns a tree of <em>nodes</em>.
           Page ids are UUID v7; node ids are server-generated strings
           (<code>n_…</code>). A node's <code>parent_id</code> links it to its parent —
           root nodes have <code>parent_id: null</code>. Status is one of
           <code>todo</code>, <code>in-progress</code>, <code>done</code>,
           <code>blocked</code>.</p>
      </section>

      ${groups}
    </div>`;

  (document.getElementById('api-back-btn') as HTMLButtonElement)
    .addEventListener('click', () => window.location.assign('/'));

  setupDomThemeToggle(document.getElementById('api-theme-toggle') as HTMLButtonElement);

  const logo = root.querySelector<HTMLElement>('.app-logo') as HTMLElement;
  logo.addEventListener('click', () => window.location.assign('/'));
  logo.addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); window.location.assign('/'); }
  });
}
